import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';

export interface PageHeaderProps {
  title: string;
  description?: ReactNode;
  /** Buttons / filters aligned to the right. */
  actions?: ReactNode;
  /** Breadcrumb or context line rendered above the title. */
  eyebrow?: ReactNode;
  /** Renders a back link in the eyebrow slot. */
  backTo?: string;
  backLabel?: string;
  /** Tabs, filter chips or a search row directly under the header. */
  children?: ReactNode;
  className?: string;
}

/**
 * Every page opens with this: title, one clarifying line, and the actions that
 * belong to the whole page. Keeps vertical rhythm identical across surfaces.
 */
export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
  backTo,
  backLabel = 'Back',
  children,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn('pb-6', className)}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="min-w-0 space-y-1">
          {backTo ? (
            <Link
              to={backTo}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-muted transition-colors hover:text-fg"
            >
              <ArrowLeft className="size-3.5" aria-hidden />
              {backLabel}
            </Link>
          ) : (
            eyebrow && <div className="text-xs font-medium text-subtle">{eyebrow}</div>
          )}
          <h1 className="text-xl font-semibold tracking-tight text-fg md:text-[1.375rem]">
            {title}
          </h1>
          {description && (
            <p className="max-w-2xl text-sm leading-relaxed text-muted">{description}</p>
          )}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children && <div className="pt-5">{children}</div>}
    </header>
  );
}
